import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useState, useEffect, useLayoutEffect } from 'react'

import eventEmitter from '../../shared/eventEmitter'
import useDialogDismiss from '../../shared/useDialogDismiss'
import { askDiscardChanges } from '../../shared/confirmDialog'

import styles from './BaseModal.module.scss'
import Icon from '../Icon'

export default function BaseModal(props) {
	const [isOpen, setOpen] = useState(props.defaultOpen)
	const [params, setParams] = useState(null)

	const handleOpen = (...args) => {
		setParams(args)
		setOpen(true)
	}

	/*
	 * Schliesst ohne Rueckfrage. Diesen Weg nehmen die Fenster selbst, nachdem
	 * gespeichert wurde — da gibt es nichts mehr zu retten.
	 */
	const closeModal = () => {
		if (props.isLoading) return

		setOpen(false)
		if (props.onClose) props.onClose()
	}

	/*
	 * Schliesst auf Wunsch der bedienenden Person (Kreuz, Klick daneben, Esc).
	 * Stehen ungespeicherte Aenderungen im Fenster, wird vorher gefragt.
	 */
	const requestClose = async () => {
		if (props.isLoading) return
		if (props.isDirty && !(await askDiscardChanges())) return

		closeModal()
	}

	const backdropProps = useDialogDismiss(requestClose, { enabled: isOpen && !props.isLoading })

	useEffect(() => {
		eventEmitter.on(props.nameEvent, handleOpen)
		return () => {
			eventEmitter.off(props.nameEvent, handleOpen)
		}
	}, [props.nameEvent])

	useLayoutEffect(() => {
		if (isOpen && props.onOpen) setTimeout(() => props.onOpen(...params))

		/* eslint-disable-next-line */
	}, [isOpen])

	if (!isOpen) return null

	return (
		<div className={clsx(styles.overlay, props.fitViewport && styles.overlayFit)} {...backdropProps}>
			<div
				style={{ maxWidth: props.maxWidth }}
				className={clsx(styles.modal, props.fitViewport && styles.modalFit)}
			>
				<div className={clsx(styles.header, styles.container)}>
					<h3 className={styles.title}>{props.title}</h3>
					{!props.hideClose && (
						<button type="button" className={styles.btnClose} onClick={requestClose}>
							<Icon name="close" size={{ w: 12, h: 12 }} />
						</button>
					)}
				</div>
				<div className={clsx(styles.container, props.fitViewport && styles.bodyFit)}>
					{props.children({ closeModal, requestClose })}
				</div>
			</div>
		</div>
	)
}

BaseModal.propTypes = {
	children: PropTypes.func.isRequired,
	nameEvent: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,

	disabledCrossClose: PropTypes.bool,
	isLoading: PropTypes.bool,

	maxWidth: PropTypes.number,
	// Dialog auf die Fensterhoehe begrenzen und den Rumpf selbst scrollen lassen
	// — der Fuss (und damit "Speichern") bleibt dadurch immer sichtbar.
	fitViewport: PropTypes.bool,
	// Stehen im Fenster ungespeicherte Aenderungen? Dann wird vor dem Schliessen
	// per Kreuz, Klick daneben oder Esc zurueckgefragt. Formulare mit
	// react-hook-form liefern das als `formState.isDirty` frei Haus.
	isDirty: PropTypes.bool,

	hideClose: PropTypes.bool,
	defaultOpen: PropTypes.bool,

	onClose: PropTypes.func,
	onOpen: PropTypes.func
}

BaseModal.defaultProps = {
	disabledCrossClose: false,
	isLoading: false,
	defaultOpen: false,
	fitViewport: false,
	isDirty: false,
	maxWidth: 522,

	hideClose: false
}
